import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Animated, Easing, Linking, Modal, Platform, Pressable, ScrollView,
  Text, View, useWindowDimensions,
} from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { CalendarCheck, Check, ShieldCheck, X } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { IDLE_COIN_SOURCE, ASSET_PATHS, getAssetUrl } from '../../../../constants/assets';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../shared/helpers/imageWebAlt.helper';
import { useEconomyStore } from '../../../shared/stores/economyStore';
import { useApiClient } from '../../../../helpers/apiClient.helper';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { useGuestGuard } from '../../../shared/hooks/useGuestGuard';
import { GUEST_AUTH_ACTIONS } from '../../../shared/hooks/useGuestGuard.constants';
import { DailyRewardModal } from '../DailyRewardModal';
import { getDailyClaimSuccessSoundId } from '../../helpers/dailyClaimGameSound.helper';
import { getCountdownDisplay } from '../../../challenges/helpers/countdownToDate.helper';
import { useSound } from '../../../shared/hooks/useSound';
import { useTheme } from '../../../theme/useTheme';
import { BundleCard } from './BundleCard';
import { PurchaseProcessingOverlay } from './PurchaseProcessingOverlay';
import { coinStoreStyles } from './CoinStoreModal.styles';
import { MAX_DAILY_CLAIM_COINS } from '../../constants/economy.constants';
import {
  BUNDLES, PALETTE, PARTICLE_COUNT, CONFETTI_COUNT, PURCHASE_WATCHDOG_MS,
  type Bundle,
} from './CoinStoreModal.constants';
import { StripeCheckoutOverlay } from '../StripeCheckoutOverlay';
import { StripeProCheckoutOverlay } from '../StripeProCheckoutOverlay';
import { ShopSegmentedControl, type ShopTab } from '../ShopSegmentedControl';
import { ProUpgradeCard } from '../ProUpgradeCard';
import { createCoinsEmbeddedCheckoutSession } from '../../helpers/coinsApi';
import { usePurchaseBundle } from '../../hooks/usePurchaseBundle';
import { usePurchaseProSubscription } from '../../hooks/usePurchaseProSubscription';
import { useSupabaseAuth } from '../../../shared/contexts/SupabaseAuthContext';
import { API_ENDPOINTS } from '../../../../constants/api';
import { useConsumerProBillingActions } from '../../../accounts/hooks/useConsumerProBillingActions';
import { isManageableConsumerSub, type ConsumerBilling } from '../../helpers/consumerProSubscription.helper';
import { getStoreSubscriptionsUrl, isStripeManagedProvider } from '../../../shared/helpers/proSubscriptionManage.helper';

import { useCounterTo } from '../../helpers/useCounterTo.hook';
import { VIRTUAL_CURRENCY_SHORT_DISCLOSURE } from '../../../shared/constants/virtualCurrency.constants';

const IS_WEB = Platform.OS === 'web';
const USE_NATIVE = !IS_WEB;
// TEMP: native iOS IAP (RevenueCat) isn't wired yet, so real-money purchases can't complete and
// would risk App Store rejection (guideline 3.1.1). Hide paid coin bundles + the Pro tab on iOS;
// keep the free "Earn coins" / daily reward. Remove once iOS IAP ships.
const HIDE_PAID_PURCHASES = Platform.OS === 'ios';

export interface CoinStoreModalProps {
  isVisible: boolean;
  onDismiss: () => void;
  /** When set, auto-fires the coin burst animation from screen center on open (used after Stripe return). */
  pendingAward?: number;
  testID?: string;
}

export const CoinStoreModal: React.FC<CoinStoreModalProps> = ({ isVisible, onDismiss, pendingAward, testID }) => {
  const theme = useTheme();
  const styles = useMemo(() => coinStoreStyles(theme), [theme]);
  const confettiColors = useMemo(
    () => [theme.accent, theme.accentLight, theme.accentDark, theme.primary, '#FF4D8F', theme.white],
    [theme],
  );
  const { width: screenW, height: screenH } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const contentWidth = Platform.OS === 'web' ? Math.min(screenW, 540) : screenW;
  const cardWidth = useMemo(() => (contentWidth - 28 - 12) / 2, [contentWidth]);

  const storeCoins = useEconomyStore((s) => s.coins);
  const claimDaily = useEconomyStore((s) => s.claimDaily);
  const { purchaseBundle, reconcileCoins } = usePurchaseBundle();
  const { purchaseProSubscription } = usePurchaseProSubscription();
  const apiClient = useApiClient();
  const { showSnackbar } = useSnackbar();
  const { requireAuth } = useGuestGuard();
  const { playSound } = useSound();
  const queryClient = useQueryClient();
  const { isLoaded, isSignedIn } = useSupabaseAuth();

  // One source of truth for Pro status, shared by query key with useIsProUser (both read the raw
  // billing snapshot so the ['proStatus'] cache entry has one consistent shape). Two distinct signals
  // come out of it — do NOT conflate them:
  //  • isPro        — holds the Pro entitlement on ANY platform (planTier === 'PRO'). Gates whether we
  //                   OFFER a purchase. A store-bought (RevenueCat) Pro opening the web must never be
  //                   invited to buy again on Stripe — that is a second, parallel charge on a different
  //                   billing rail that neither side knows about.
  //  • isManageable — has a real, manageable subscription on THIS surface (web: active/trialing/past_due
  //                   Stripe sub + a Stripe customer; native: the entitlement, managed in the OS store).
  //                   Gates the "Manage subscription" CTA (web → Stripe portal; native → store settings).
  const { data: consumerBilling = null } = useQuery({
    queryKey: ['proStatus'],
    queryFn: async () => {
      const res = await (apiClient as (endpoint: string) => Promise<{ data: unknown }>)(
        API_ENDPOINTS.accounts.user,
      );
      return (res.data as { data?: { consumerBilling?: ConsumerBilling | null } | null })?.data?.consumerBilling ?? null;
    },
    enabled: Boolean(isLoaded && isSignedIn),
    staleTime: 30 * 1000,
  });
  const isPro = consumerBilling?.planTier === 'PRO';
  const isManageable = IS_WEB ? isManageableConsumerSub(consumerBilling) : isPro;

  const { openConsumerProPortal } = useConsumerProBillingActions();

  // Start counter at pre-award balance so it animates up when the burst fires.
  const [coinTarget, setCoinTarget] = useState(() => storeCoins - (pendingAward ?? 0));
  const displayedCoins = useCounterTo(coinTarget);
  const [shopTab, setShopTab] = useState<ShopTab>('coins');
  const [stripeSession, setStripeSession] = useState<{ clientSecret: string; sessionId: string } | null>(null);
  const [stripeProSession, setStripeProSession] = useState<{ clientSecret: string; sessionId: string } | null>(null);
  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [purchasingPro, setPurchasingPro] = useState(false);
  // Synchronous re-entrancy lock. State updates (and the blocking overlay) only land on the next
  // render, so a fast double-tap can slip two purchases through before they take effect — this ref
  // is checked/set inline so a second purchase is impossible regardless of render timing.
  const busyRef = useRef(false);
  // Watchdog: after PURCHASE_WATCHDOG_MS still-busy, reveal a neutral "Dismiss" on the overlay so a
  // stuck spinner (e.g. the OS payment sheet never returning) can never permanently strand the UI.
  const [showProcessingEscape, setShowProcessingEscape] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [gainAmount, setGainAmount] = useState(0);
  const [dailyModalVisible, setDailyModalVisible] = useState(false);
  const [burstSrc, setBurstSrc] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // ── Wallet bump ─────────────────────────────────────────────
  const walletScale = useRef(new Animated.Value(1)).current;
  const gainOp = useRef(new Animated.Value(0)).current;
  const gainY = useRef(new Animated.Value(0)).current;

  // ── Banner shimmer ──────────────────────────────────────────
  const bannerShimX = useRef(new Animated.Value(-200)).current;

  // ── Particles ───────────────────────────────────────────────
  const walletRef = useRef<View>(null);
  const particles = useRef(
    Array.from({ length: PARTICLE_COUNT }, () => ({
      x: new Animated.Value(0), y: new Animated.Value(0),
      op: new Animated.Value(0), sc: new Animated.Value(0.4),
    }))
  ).current;
  const confetti = useRef(
    Array.from({ length: CONFETTI_COUNT }, () => ({
      x: new Animated.Value(0), y: new Animated.Value(0),
      rot: new Animated.Value(0), op: new Animated.Value(0),
    }))
  ).current;

  useEffect(() => {
    if (!isVisible) return;
    // When opening normally (no pending award), sync counter to current balance.
    if (!pendingAward || pendingAward <= 0) setCoinTarget(storeCoins);
    const anim = Animated.loop(Animated.sequence([
      Animated.timing(bannerShimX, { toValue: screenW + 80, duration: 2200, useNativeDriver: USE_NATIVE }),
      Animated.delay(2300),
      Animated.timing(bannerShimX, { toValue: -200, duration: 0, useNativeDriver: USE_NATIVE }),
    ]));
    anim.start();
    return () => anim.stop();
  }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // On open (native, signed-in): reconcile any uncredited RevenueCat coin purchases — covers an app
  // killed before the post-purchase sync, and heals users who paid before this fix shipped. No-op on web.
  useEffect(() => {
    if (IS_WEB || !isVisible || !isLoaded || !isSignedIn) return;
    void (async () => {
      const newCoins = await reconcileCoins();
      if (newCoins > 0) setCoinTarget(newCoins);
    })();
  }, [isVisible, isLoaded, isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep shop header wallet in sync while daily reward is open (claim updates economy store).
  useEffect(() => {
    if (!isVisible || !dailyModalVisible || purchasingId !== null || gainAmount > 0) return;
    setCoinTarget(storeCoins);
  }, [storeCoins, isVisible, dailyModalVisible, purchasingId, gainAmount]);

  // Auto-fire burst animation when modal opens with a pending award (e.g. after Stripe return).
  // 900ms lets the modal slide-in animation (600ms) complete before measuring walletRef.
  useEffect(() => {
    if (!isVisible || !pendingAward || pendingAward <= 0) return;
    const t = setTimeout(() => {
      fireBurstFromCenter(pendingAward, `+${pendingAward.toLocaleString()} coins`);
    }, 900);
    return () => clearTimeout(t);
  }, [isVisible, pendingAward]); // eslint-disable-line react-hooks/exhaustive-deps

  const fireBurstFromCoords = useCallback((srcCX: number, srcCY: number, dx: number, dy: number, awarded: number, label: string) => {
    setBurstSrc({ x: srcCX - 13, y: srcCY - 13 });

    const burstAnims = particles.map((p, i) => {
      const spread = (Math.random() - 0.5) * 220;
      const arc = -100 - Math.random() * 100;
      const dur = 700 + Math.random() * 280;
      p.x.setValue(0); p.y.setValue(0); p.op.setValue(0); p.sc.setValue(0.4);
      return Animated.sequence([
        Animated.delay(i * 22),
        Animated.parallel([
          Animated.sequence([
            Animated.timing(p.x, { toValue: spread, duration: dur * 0.38, useNativeDriver: USE_NATIVE }),
            Animated.timing(p.x, { toValue: dx, duration: dur * 0.62, useNativeDriver: USE_NATIVE }),
          ]),
          Animated.sequence([
            Animated.timing(p.y, { toValue: arc, duration: dur * 0.38, useNativeDriver: USE_NATIVE }),
            Animated.timing(p.y, { toValue: dy, duration: dur * 0.62, useNativeDriver: USE_NATIVE }),
          ]),
          Animated.sequence([
            Animated.timing(p.op, { toValue: 1, duration: 120, useNativeDriver: USE_NATIVE }),
            Animated.delay(dur * 0.68),
            Animated.timing(p.op, { toValue: 0, duration: dur * 0.32, useNativeDriver: USE_NATIVE }),
          ]),
          Animated.timing(p.sc, { toValue: 0.9 + Math.random() * 0.3, duration: dur * 0.4, useNativeDriver: USE_NATIVE }),
        ]),
      ]);
    });

    const confettiAnims = confetti.map((c) => {
      const xStart = 20 + Math.random() * (screenW - 40);
      const xDrift = (Math.random() - 0.5) * 140;
      c.x.setValue(xStart); c.y.setValue(-20); c.op.setValue(0); c.rot.setValue(0);
      return Animated.sequence([
        Animated.delay(Math.random() * 240),
        Animated.parallel([
          Animated.timing(c.y, { toValue: 600 + Math.random() * 280, duration: 1600 + Math.random() * 400, useNativeDriver: USE_NATIVE }),
          Animated.timing(c.x, { toValue: xStart + xDrift, duration: 1800, useNativeDriver: USE_NATIVE }),
          Animated.timing(c.rot, { toValue: 720, duration: 2000, useNativeDriver: USE_NATIVE }),
          Animated.sequence([
            Animated.timing(c.op, { toValue: 1, duration: 200, useNativeDriver: USE_NATIVE }),
            Animated.timing(c.op, { toValue: 0, duration: 600, delay: 1000, useNativeDriver: USE_NATIVE }),
          ]),
        ]),
      ]);
    });

    Animated.parallel([...burstAnims, ...confettiAnims]).start();

    setTimeout(() => {
      setCoinTarget((prev) => prev + awarded);
      setGainAmount(awarded);
      Animated.sequence([
        Animated.spring(walletScale, { toValue: 1.14, damping: 6, stiffness: 300, useNativeDriver: USE_NATIVE }),
        Animated.spring(walletScale, { toValue: 1, damping: 10, stiffness: 200, useNativeDriver: USE_NATIVE }),
      ]).start();
      gainOp.setValue(0); gainY.setValue(0);
      Animated.parallel([
        Animated.sequence([
          Animated.timing(gainOp, { toValue: 1, duration: 200, useNativeDriver: USE_NATIVE }),
          Animated.delay(800),
          Animated.timing(gainOp, { toValue: 0, duration: 600, useNativeDriver: USE_NATIVE }),
        ]),
        Animated.timing(gainY, { toValue: -28, duration: 1600, easing: Easing.out(Easing.quad), useNativeDriver: USE_NATIVE }),
      ]).start();
    }, 540);

    setToastMsg(label);
    setTimeout(() => setToastMsg(null), 2400);
  }, [particles, confetti, walletScale, gainOp, gainY, screenW]);

  const fireBurst = useCallback((sourceRef: React.RefObject<View | null>, awarded: number, label: string) => {
    if (!walletRef.current || !sourceRef.current) return;
    walletRef.current.measure((_x, _y, wW, wH, walletPageX, walletPageY) => {
      sourceRef.current!.measure((_x2, _y2, sW, sH, srcPageX, srcPageY) => {
        const srcCX = srcPageX + sW / 2;
        const srcCY = srcPageY + sH / 2;
        const dx = walletPageX + wW / 2 - srcCX;
        const dy = walletPageY + wH / 2 - srcCY;
        fireBurstFromCoords(srcCX, srcCY, dx, dy, awarded, label);
      });
    });
  }, [fireBurstFromCoords]);

  const fireBurstFromCenter = useCallback((awarded: number, label: string) => {
    if (!walletRef.current) return;
    walletRef.current.measure((_x, _y, wW, wH, walletPageX, walletPageY) => {
      const srcCX = screenW / 2;
      const srcCY = screenH * 0.62;
      const dx = walletPageX + wW / 2 - srcCX;
      const dy = walletPageY + wH / 2 - srcCY;
      fireBurstFromCoords(srcCX, srcCY, dx, dy, awarded, label);
    });
  }, [fireBurstFromCoords, screenW, screenH]);

  const handleStartProCheckout = useCallback(async (billingInterval: 'month' | 'year') => {
    if (busyRef.current) return;
    if (!requireAuth(GUEST_AUTH_ACTIONS.DAILY_REWARD)) return;
    busyRef.current = true;
    setPurchasingPro(true);
    try {
      const result = await purchaseProSubscription(billingInterval);
      if (!result.success) {
        showSnackbar(result.error ?? 'Could not start checkout.', 'error', 'Pro');
        return;
      }
      if (IS_WEB && result.session) {
        setStripeProSession(result.session);
      } else {
        // Native: purchase is done inside usePurchaseProSubscription
        await queryClient.invalidateQueries({ queryKey: ['proStatus'] });
        showSnackbar('Pro activated!', 'success', 'Pro');
      }
    } catch (err: unknown) {
      showSnackbar(err instanceof Error ? err.message : 'Purchase failed.', 'error', 'Pro');
    } finally {
      setPurchasingPro(false);
      busyRef.current = false;
    }
  }, [requireAuth, purchaseProSubscription, showSnackbar, queryClient]);

  const handleManagePro = useCallback(() => {
    // Route by the rail that owns the sub, NOT by the current platform: a Stripe sub → Stripe portal
    // (web-only; on native the portal hook tells the user to manage on the web), a store sub → the OS
    // store settings. Routing the wrong way opens an empty portal / store.
    //
    // `proProvider` alone is NOT a reliable signal: the column is new (added 2026-06) and only gets
    // stamped 'stripe' by the subscription webhook, so an existing Stripe subscriber can still be null
    // — or even mislabelled 'revenuecat' after a cross-rail sync — while holding a live Stripe sub. On
    // web, `isManageableConsumerSub` proves an active Stripe sub + customer; trust THAT and open the
    // portal, so a real Stripe subscriber is never wrongly deep-linked to an app store.
    const provider = consumerBilling?.proProvider ?? null;
    const stripeManageable = isStripeManagedProvider(provider)
      || (IS_WEB && isManageableConsumerSub(consumerBilling));
    if (stripeManageable) {
      // When the store was opened from inside /app, return there afterwards (the portal-return handler
      // in app/app/_layout syncs + shows a snackbar); otherwise the portal defaults to /packs.
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const returnPath = path.startsWith('/app') ? path : undefined;
      void openConsumerProPortal(returnPath != null ? { returnPath } : undefined);
      return;
    }
    if (IS_WEB) {
      // On web there is no OS store sheet to open, and a non-Stripe Pro was necessarily bought in the
      // mobile app — never deep-link a browser to Google Play / App Store subscriptions here.
      showSnackbar('You bought Pro in the mobile app — manage your subscription there.', 'info', 'Pro');
      return;
    }
    void Linking.openURL(getStoreSubscriptionsUrl(provider));
  }, [openConsumerProPortal, consumerBilling, showSnackbar]);

  const handleBuy = useCallback(async (bundle: Bundle, ref: React.RefObject<View | null>) => {
    if (busyRef.current) return;
    if (!requireAuth(GUEST_AUTH_ACTIONS.DAILY_REWARD)) return;
    busyRef.current = true;
    setPurchasingId(bundle.id);
    try {
      if (IS_WEB) {
        const session = await createCoinsEmbeddedCheckoutSession(
          apiClient as (endpoint: string, options?: Record<string, unknown>) => Promise<{ status: number; data: unknown }>,
          bundle.id,
        );
        setStripeSession(session);
      } else {
        const result = await purchaseBundle(bundle.id);
        if (result.success) {
          const awarded = result.awarded ?? bundle.coins;
          fireBurst(ref, awarded, `+${awarded.toLocaleString()} coins`);
        } else {
          showSnackbar(result.error ?? 'Purchase failed. Try again.', 'error', 'Shop');
        }
      }
    } catch (err: unknown) {
      showSnackbar(err instanceof Error ? err.message : 'Purchase failed. Try again.', 'error', 'Shop');
    } finally {
      setPurchasingId(null);
      busyRef.current = false;
    }
  }, [requireAuth, apiClient, purchaseBundle, fireBurst, showSnackbar]);

  const handleDailyRewardClaim = useCallback(async () => {
    const preClaim = useEconomyStore.getState();
    const successSoundId = getDailyClaimSuccessSoundId(preClaim.dailyClaimStreak, preClaim.lastDailyClaimAt);
    const result = await claimDaily(
      apiClient as (endpoint: string, options?: Record<string, unknown>) => Promise<{ status: number; data: unknown }>
    );
    if (result.success) playSound(successSoundId);
    if (!result.success) {
      if (result.error === 'SIGN_IN_REQUIRED') { requireAuth(GUEST_AUTH_ACTIONS.DAILY_REWARD); return; }
      if (result.retryAfterMs) {
        showSnackbar(`Available in ${getCountdownDisplay(new Date(Date.now() + result.retryAfterMs))}. Resets at UTC midnight.`, 'info', 'Daily Claim');
      } else {
        showSnackbar(result.error ?? 'Could not claim coins right now.', 'error', 'Daily Claim');
      }
    }
  }, [claimDaily, apiClient, playSound, requireAuth, showSnackbar]);

  const isProcessing = purchasingId !== null || purchasingPro;

  // Watchdog: arm a timer while a purchase is in flight; if it's still going after the grace period,
  // expose the overlay's escape hatch. Resets whenever processing ends.
  useEffect(() => {
    if (!isProcessing) {
      setShowProcessingEscape(false);
      return;
    }
    const t = setTimeout(() => setShowProcessingEscape(true), PURCHASE_WATCHDOG_MS);
    return () => clearTimeout(t);
  }, [isProcessing]);

  // Manual escape from a stuck overlay. Clears the busy UI but never claims failure — the purchase
  // may still complete, so heal in the background (reconcile coins / refetch Pro) and tell the user.
  const handleDismissProcessing = useCallback(() => {
    setPurchasingId(null);
    setPurchasingPro(false);
    busyRef.current = false;
    setShowProcessingEscape(false);
    if (!IS_WEB) {
      void reconcileCoins().then((newCoins) => { if (newCoins > 0) setCoinTarget(newCoins); });
      void queryClient.invalidateQueries({ queryKey: ['proStatus'] });
    }
    showSnackbar('Still processing — if your purchase went through it will appear shortly.', 'info', 'Shop');
  }, [reconcileCoins, queryClient, showSnackbar]);

  return (
    <>
      {/* Hide the Shop while the daily-reward modal is open: two stacked native Modals break
          touch handling on iOS (the top modal's buttons stop responding). DailyRewardModal is a
          sibling below, so hiding this one leaves a single presented modal. */}
      <Modal
        visible={isVisible && !dailyModalVisible}
        transparent={false}
        animationType="slide"
        onRequestClose={() => { if (!busyRef.current) onDismiss(); }}
        statusBarTranslucent
        presentationStyle="fullScreen"
        testID={testID ?? 'coin-store-modal'}
      >
        <View style={styles.bgFill}>
          <LinearGradient
            colors={[PALETTE.bgTop, PALETTE.bgMid, PALETTE.bgBot]}
            locations={[0, 0.3, 1]}
            style={styles.gradientFill}
          />

          <View style={styles.contentWrap}>
          {/* Header */}
          <View style={[styles.header, { paddingTop: insets.top + 14 }]}>
            <Animated.View
              ref={walletRef}
              collapsable={false}
              style={[styles.walletChip, { transform: [{ scale: walletScale }] }]}
            >
              <Image source={IDLE_COIN_SOURCE} style={styles.walletCoinIcon} alt={WEB_IMAGE_ALT_DECORATIVE} />
              <Text style={styles.walletValue}>{displayedCoins.toLocaleString()}</Text>
              <Animated.View style={[styles.gainLabel, { opacity: gainOp, transform: [{ translateY: gainY }] }]}>
                <Text style={styles.gainLabelText}>+{gainAmount.toLocaleString()}</Text>
              </Animated.View>
            </Animated.View>
            <Text style={styles.headerTitle}>Shop</Text>
            <Pressable style={styles.closeBtn} onPress={onDismiss} accessibilityRole="button" accessibilityLabel="Close shop">
              <X size={18} color={theme.gray[600]} />
            </Pressable>
          </View>

          {/* Tab toggle — hidden on iOS while paid purchases (Pro) are unavailable */}
          {!HIDE_PAID_PURCHASES && (
            <View style={styles.segmentedWrap}>
              <ShopSegmentedControl value={shopTab} onChange={setShopTab} />
            </View>
          )}

          {/* Body */}
          <ScrollView
            style={styles.flex1}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {!HIDE_PAID_PURCHASES && shopTab === 'pro' ? (
              <ProUpgradeCard
                isPro={isPro}
                isManageable={isManageable}
                onPurchase={handleStartProCheckout}
                onManage={handleManagePro}
                loading={purchasingPro}
              />
            ) : (
            <>
            {/* Banner + paid bundle grid — hidden on iOS while IAP is unavailable */}
            {!HIDE_PAID_PURCHASES && (
              <>
              <View style={styles.bannerWrap}>
                <Image
                  source={{ uri: getAssetUrl(ASSET_PATHS.COIN_STORE_BANNER) }}
                  style={styles.bannerImage}
                  contentFit="cover"
                  alt="Recharge"
                />
                <Animated.View style={[styles.bannerShimmerStrip, { transform: [{ translateX: bannerShimX }] }]}>
                  <LinearGradient
                    colors={['transparent', 'rgba(255,255,255,0.18)', 'transparent']}
                    start={{ x: 0, y: 0.5 }} end={{ x: 1, y: 0.5 }}
                    style={styles.bannerShimmerGradient}
                  />
                </Animated.View>
              </View>

              <View style={styles.grid}>
                {BUNDLES.map((b) => (
                  <BundleCard
                    key={b.id}
                    bundle={b}
                    cardWidth={cardWidth}
                    onBuy={handleBuy}
                    loading={purchasingId === b.id}
                  />
                ))}
              </View>
              </>
            )}

            {/* Earn free coins — daily reward only */}
            <View style={styles.sectHeader}>
              <Text style={styles.sectTitle}>Earn free coins</Text>
              <Text style={styles.sectMeta}>No purchase needed</Text>
            </View>

            <Pressable
              style={({ pressed }) => [styles.earnCard, pressed && styles.earnCardPressed]}
              onPress={() => {
                if (!requireAuth(GUEST_AUTH_ACTIONS.DAILY_REWARD)) return;
                setDailyModalVisible(true);
              }}
              accessibilityRole="button"
              accessibilityLabel="Open daily reward"
            >
              <View style={styles.earnIcon}>
                <CalendarCheck size={22} color={PALETTE.earnIconBorder} strokeWidth={2.6} />
              </View>
              <View style={styles.earnCardText}>
                <Text style={styles.earnTitle}>Daily reward</Text>
                <Text style={styles.earnSub}>Day streak · up to {MAX_DAILY_CLAIM_COINS.toLocaleString()} coins</Text>
              </View>
              <View style={styles.earnCta}>
                <Text style={styles.earnCtaText}>Claim</Text>
              </View>
            </Pressable>

            {/* Footer */}
            <View style={styles.footer}>
              {!HIDE_PAID_PURCHASES && (
                <View style={styles.footerRow}>
                  <ShieldCheck size={12} color={theme.gray[400]} strokeWidth={2.4} />
                  <Text style={styles.footerText}>Secure checkout</Text>
                </View>
              )}
              {/* Same text as the Terms' virtual-currency section, from one constant, so the shop
                  and the published Terms cannot drift. Shown on every platform, including iOS
                  where paid bundles are hidden. */}
              <Text style={styles.footerText} testID="coin-store-virtual-currency-disclosure">
                {VIRTUAL_CURRENCY_SHORT_DISCLOSURE}
              </Text>
            </View>
            </>
            )}
          </ScrollView>

          {/* Toast */}
          {toastMsg !== null && (
            <Animated.View style={[styles.toast, { top: insets.top + 80 }]}>
              <View style={styles.toastCheck}>
                <Check size={14} color={PALETTE.mintInk} strokeWidth={3} />
              </View>
              <View>
                <Text style={styles.toastLabel}>PURCHASED</Text>
                <Text style={styles.toastText}>{toastMsg}</Text>
              </View>
            </Animated.View>
          )}
          </View>

          {/* Particle layer */}
          <View style={styles.particleLayer} pointerEvents="none">
            {particles.map((p, i) => (
              <Animated.View key={i} style={[styles.particle, {
                left: burstSrc.x, top: burstSrc.y,
                opacity: p.op,
                transform: [{ translateX: p.x }, { translateY: p.y }, { scale: p.sc }],
              }]}>
                <Image source={IDLE_COIN_SOURCE} style={styles.particleImg} alt="" contentFit="contain" />
              </Animated.View>
            ))}
            {confetti.map((c, i) => (
              <Animated.View key={i} style={[styles.confettiPiece, {
                width: i % 3 === 0 ? 8 : 7,
                height: i % 3 === 0 ? 8 : 11,
                borderRadius: i % 2 === 0 ? 4 : 2,
                backgroundColor: confettiColors[i % confettiColors.length],
                opacity: c.op,
                transform: [
                  { translateX: c.x },
                  { translateY: c.y },
                  { rotate: c.rot.interpolate({ inputRange: [0, 720], outputRange: ['0deg', '720deg'] }) },
                ],
              }]} />
            ))}
          </View>

          {/* Blocks all interaction while a purchase is in flight (prevents a second charge). */}
          <PurchaseProcessingOverlay
            visible={isProcessing}
            message={purchasingPro ? 'Activating Pro…' : 'Processing your purchase…'}
            onDismiss={showProcessingEscape ? handleDismissProcessing : undefined}
            dismissLabel="Dismiss"
          />
        </View>
      </Modal>

      <DailyRewardModal
        isVisible={dailyModalVisible}
        onDismiss={() => setDailyModalVisible(false)}
        onClaim={handleDailyRewardClaim}
      />

      {stripeProSession && (
        <StripeProCheckoutOverlay
          clientSecret={stripeProSession.clientSecret}
          sessionId={stripeProSession.sessionId}
          onComplete={async () => {
            setStripeProSession(null);
            await queryClient.invalidateQueries({ queryKey: ['proStatus'] });
          }}
          onCancel={() => setStripeProSession(null)}
        />
      )}

      {stripeSession && (
        <StripeCheckoutOverlay
          clientSecret={stripeSession.clientSecret}
          sessionId={stripeSession.sessionId}
          onComplete={(awarded) => {
            setStripeSession(null);
            if (awarded > 0) {
              // Delay one tick so React commits the state update (removes Stripe overlay
              // from DOM) before measure() runs — otherwise walletRef layout is stale.
              setTimeout(() => {
                fireBurstFromCenter(awarded, `+${awarded.toLocaleString()} coins`);
              }, 50);
            }
            // Always sync the counter — handles awarded=0 (webhook raced and credited
            // coins before this sync) and is the fallback if measure() never fires.
            setTimeout(() => {
              setCoinTarget(useEconomyStore.getState().coins);
            }, awarded > 0 ? 700 : 50);
          }}
          onCancel={() => setStripeSession(null)}
        />
      )}
    </>
  );
};
